'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Download } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import type { MenuProps } from '@mui/material/Menu';
import {
  CascadingHoverMenus,
  CascadingMenuItem,
  CascadingSubmenu,
} from '@prismo-io/design-system';
import {
  LanguageCode,
  type LanguageCodeT,
  type ToolsOrganizationsT,
  type UsersT,
} from '@prismo-io/schemas';
import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { DownloadPdfButton } from './download-pdf-button';
import {
  type Config,
  DEFAULT_CONFIG,
  TypeSchema,
  type TypeSchemaT,
} from './schema';
import { useDownloadPDFUser } from './use-download-pdf-user';

type Props = {
  organization?: Pick<ToolsOrganizationsT, 'id' | 'email'>;
  users: Pick<UsersT, 'id' | 'full_name'>[];
  disabled?: boolean;
  config?: Config;
  children?: (props: unknown) => ReactNode;
} & Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'>;

export const DownloadPdfUserButton: FC<Props> = (props) => {
  const {
    organization,
    users,
    disabled = false,
    config,
    anchorOrigin,
    transformOrigin,
    children = (props: any) => (
      <IconButton
        {...props}
        disabled={disabled}
        sx={{ opacity: disabled ? 0.5 : 1 }}
      >
        <Download color="gray" />
      </IconButton>
    ),
  } = props;

  const { cv, expert, talent, mode, canChooseLocale } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  const { i18n } = useLingui();

  const download = useDownloadPDFUser();

  const onClickMenuItem = useCallback(
    (locale: LanguageCodeT, type: TypeSchemaT) => {
      return () => {
        download({
          organization,
          users,
          locale,
          type,
        } as any);
      };
    },
    [download, organization, users]
  );

  const onlyOneIsVisible = useMemo(
    () =>
      [cv?.visible, talent?.visible, expert?.visible].filter(
        (visible) => visible === true
      ).length === 1,
    [cv, talent, expert]
  );

  const oneLeastIsVisible = useMemo(
    () => cv?.visible || talent?.visible || expert?.visible,
    [cv, talent, expert]
  );

  if (!oneLeastIsVisible) {
    return null;
  }

  if (mode === 'all') {
    return (
      <DownloadPdfButton
        organization={organization}
        users={users}
        disabled={disabled}
        config={config}
        children={children}
      />
    );
  }

  if (!canChooseLocale) {
    if (onlyOneIsVisible) {
      return (
        <>
          {cv?.visible && (
            <CascadingMenuItem
              onClick={onClickMenuItem(
                i18n.locale as LanguageCodeT,
                TypeSchema.Enum.CV
              )}
              disabled={cv.disabled}
            >
              <Trans>CV PRISMO</Trans>
            </CascadingMenuItem>
          )}
          {talent?.visible && (
            <CascadingMenuItem
              onClick={onClickMenuItem(
                i18n.locale as LanguageCodeT,
                TypeSchema.Enum.TALENT
              )}
              disabled={talent.disabled}
            >
              <Trans>Talent</Trans>
            </CascadingMenuItem>
          )}
          {expert?.visible && (
            <CascadingMenuItem
              onClick={onClickMenuItem(
                i18n.locale as LanguageCodeT,
                TypeSchema.Enum.EXPERT
              )}
              disabled={expert.disabled}
            >
              <Trans>Expert</Trans>
            </CascadingMenuItem>
          )}
        </>
      );
    }

    return (
      <CascadingHoverMenus
        popupId="download"
        variant="popover"
        button={children}
        cascadingMenuProps={{
          anchorOrigin,
          transformOrigin,
        }}
      >
        {cv?.visible && (
          <CascadingMenuItem
            onClick={onClickMenuItem(
              i18n.locale as LanguageCodeT,
              TypeSchema.Enum.CV
            )}
            disabled={cv.disabled}
          >
            <Trans>CV PRISMO</Trans>
          </CascadingMenuItem>
        )}
        {talent?.visible && (
          <CascadingMenuItem
            onClick={onClickMenuItem(
              i18n.locale as LanguageCodeT,
              TypeSchema.Enum.TALENT
            )}
            disabled={talent.disabled}
          >
            <Trans>Talent</Trans>
          </CascadingMenuItem>
        )}
        {expert?.visible && (
          <CascadingMenuItem
            onClick={onClickMenuItem(
              i18n.locale as LanguageCodeT,
              TypeSchema.Enum.EXPERT
            )}
            disabled={expert.disabled}
          >
            <Trans>Expert</Trans>
          </CascadingMenuItem>
        )}
      </CascadingHoverMenus>
    );
  }

  return (
    <CascadingHoverMenus
      popupId="download"
      variant="popover"
      button={children}
      cascadingMenuProps={{
        anchorOrigin,
        transformOrigin,
      }}
    >
      {cv?.visible && (
        <CascadingSubmenu
          popupId="pdf-menu-cv-prismo"
          chevron={transformOrigin?.horizontal === 'right' ? 'left' : 'right'}
          title={t`CV PRISMO`}
          disabled={cv.disabled}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <CascadingMenuItem
            onClick={onClickMenuItem(LanguageCode.Enum.fr, TypeSchema.Enum.CV)}
          >
            <Trans>Français</Trans>
          </CascadingMenuItem>
          <CascadingMenuItem
            onClick={onClickMenuItem(LanguageCode.Enum.en, TypeSchema.Enum.CV)}
          >
            <Trans>Anglais</Trans>
          </CascadingMenuItem>
        </CascadingSubmenu>
      )}
      {talent?.visible && (
        <CascadingSubmenu
          popupId="pdf-menu-talent"
          chevron={transformOrigin?.horizontal === 'right' ? 'left' : 'right'}
          title={t`Talent`}
          disabled={talent.disabled}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <CascadingMenuItem
            onClick={onClickMenuItem(
              LanguageCode.Enum.fr,
              TypeSchema.Enum.TALENT
            )}
          >
            <Trans>Français</Trans>
          </CascadingMenuItem>
          <CascadingMenuItem
            onClick={onClickMenuItem(
              LanguageCode.Enum.en,
              TypeSchema.Enum.TALENT
            )}
          >
            <Trans>Anglais</Trans>
          </CascadingMenuItem>
        </CascadingSubmenu>
      )}
      {expert?.visible && (
        <CascadingSubmenu
          popupId="pdf-menu-expert"
          chevron={transformOrigin?.horizontal === 'right' ? 'left' : 'right'}
          title={t`Expert`}
          disabled={expert.disabled}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <CascadingMenuItem
            onClick={onClickMenuItem(
              LanguageCode.Enum.fr,
              TypeSchema.Enum.EXPERT
            )}
          >
            <Trans>Français</Trans>
          </CascadingMenuItem>
          <CascadingMenuItem
            onClick={onClickMenuItem(
              LanguageCode.Enum.en,
              TypeSchema.Enum.EXPERT
            )}
          >
            <Trans>Anglais</Trans>
          </CascadingMenuItem>
        </CascadingSubmenu>
      )}
    </CascadingHoverMenus>
  );
};
